import IBaseWorldData from '../System/World/Data/IBaseWorldData';
import Game from '../Game';
import { IFieldData } from '../State/World/IFieldData';
import { MAX_WORLDS } from '../Constants/World';
import { WinCriteriaType } from '../Constants/WinCriteria';
import { IWinCriteriaData } from '../State/Level/ILevelState';
import { ILimit } from './MathHelper';
import levelsData from '../../resource/data/levelsData.json';
import { report } from 'report';
import { Model } from './layout/types';
import { Layout } from "Utility/layout/createLayout";
import { WorldLayout1 } from "Layouts/WorldMaps/WorldLayout1";
import { WorldLayout19 } from "Layouts/WorldMaps/WorldLayout19";
import { WorldLayout18 } from "Layouts/WorldMaps/WorldLayout18";
import { WorldLayout17 } from "Layouts/WorldMaps/WorldLayout17";
import { WorldLayout16 } from "Layouts/WorldMaps/WorldLayout16";
import { WorldLayout15 } from "Layouts/WorldMaps/WorldLayout15";
import { WorldLayout14 } from "Layouts/WorldMaps/WorldLayout14";
import { WorldLayout13 } from "Layouts/WorldMaps/WorldLayout13";
import { WorldLayout12 } from "Layouts/WorldMaps/WorldLayout12";
import { WorldLayout11 } from "Layouts/WorldMaps/WorldLayout11";
import { WorldLayout10 } from "Layouts/WorldMaps/WorldLayout10";
import { WorldLayout9 } from "Layouts/WorldMaps/WorldLayout9";
import { WorldLayout8 } from "Layouts/WorldMaps/WorldLayout8";
import { WorldLayout7 } from "Layouts/WorldMaps/WorldLayout7";
import { WorldLayout6 } from "Layouts/WorldMaps/WorldLayout6";
import { WorldLayout5 } from "Layouts/WorldMaps/WorldLayout5";
import { WorldLayout4 } from "Layouts/WorldMaps/WorldLayout4";
import { WorldLayout3 } from "Layouts/WorldMaps/WorldLayout3";
import { WorldLayout2 } from "Layouts/WorldMaps/WorldLayout2";
import { WorldLayout20 } from "Layouts/WorldMaps/WorldLayout20";

/**
 * Temporary map to ensure Layouts are included in the build and can be selected by index.
 */
const worldLayouts = [
    WorldLayout1,
    WorldLayout2,
    WorldLayout3,
    WorldLayout4,
    WorldLayout5,
    WorldLayout6,
    WorldLayout7,
    WorldLayout8,
    WorldLayout9,
    WorldLayout10,
    WorldLayout11,
    WorldLayout12,
    WorldLayout13,
    WorldLayout14,
    WorldLayout15,
    WorldLayout16,
    WorldLayout17,
    WorldLayout18,
    WorldLayout19,
    WorldLayout20
];

export namespace WorldDataHelper {
    let _maxWorld: number;

    export function queueWorldData() {
        for (let i = 1; i <= MAX_WORLDS; i += 1) {
            Game.resource.add(`worldData${i}`, `resource/data/world/world_${i}.json`);
        }
    }

    export function getWorldNumber(levelNumber: number): number {
        const start = 1;
        const maxWorldNumber = maxWorld();
        for (let i = start; i <= maxWorldNumber; i += 1) {
            const { min, max } = getWorldLevelLimits(i);
            if (levelNumber < min) return i - 1;
            if (levelNumber >= min && levelNumber <= max) return i;
        }
        return maxWorldNumber + 1;
    }

    export function getWorldData(worldNumber: number): IBaseWorldData {
        if (Game.resource.existsAndLoaded(`worldData${worldNumber}`)) {
            return Game.resource.get<IBaseWorldData>(`worldData${worldNumber}`);
        }
        return undefined;
    }

    export function createWorldLayout(worldNumber: number) {
        const layout = worldLayouts[worldNumber - 1];
        return new layout();
    }

    export function getCropModels(worldNumber: number, fieldIndex: number): Model[] {
        const layout = worldLayouts[worldNumber - 1];
        const raw = layout.raw as any;
        const cropsContainerA = raw.list.filter((m: Model) => m.className === 'cropsSectorA')[fieldIndex];
        const cropsContainerB = raw.list.filter((m: Model) => m.className === 'cropsSectorB')[fieldIndex];

        return raw.list.filter((m: Model) => m.parent === cropsContainerA.id || m.parent === cropsContainerB.id);
    }

    export function getTotalWorldCount(): number {
        let worldNumber: number = 1;
        while (true) {
            if (Game.resource.existsOrQueued(`worldData${worldNumber}`)) {
                worldNumber++;
            } else {
                return --worldNumber;
            }
        }
    }

    export function getWorldLevelLimits(worldNumber: number): ILimit {
        const worldData: IBaseWorldData = getWorldData(worldNumber);
        try {
            if (worldData !== undefined) return worldData.limits;

            throw new Error('World data not found.')
        } catch (e){
            report(e.message, {
                once: true,
                sampleRate: 0.1,
                tags: {
                    worldNumber
                },
                fingerprint: "getWorldLevelLimits:DataNotFound"
            });
        }
    }

    export function canGetWorldLevelLimits(worldNumber: number): Boolean {
        return getWorldData(worldNumber) !== undefined;
    }

    export function getFieldData(worldNumber?: number): IFieldData[] {
        if (worldNumber) {
            return getWorldFieldData(worldNumber);
        } else {
            const maxWorldNumber: number = maxWorld();
            let fieldData: IFieldData[] = [];
            for (let i = 1; i < maxWorldNumber + 1; i++) {
                const worldFieldData: IFieldData[] = getWorldFieldData(i);

                fieldData = worldFieldData ? [...fieldData, ...worldFieldData] : fieldData;
            }
            return fieldData;
        }
    }
    export function getWorldFieldData(worldNumber: number): IFieldData[] {
        const worldDataKey: string = `worldData${worldNumber}`;

        if (!Game.resource.existsAndLoaded(worldDataKey)) return undefined;

        const worldData = Game.resource.get<IBaseWorldData>(worldDataKey);

        return worldData.fields.slice().sort((a: IFieldData, b: IFieldData) => a.requiredLevel[0] - b.requiredLevel[0]);
    }

    export function getAllFieldData(world?:number): IFieldData[] {
        const maxWorldNumber = world ?? maxWorld();
        let fieldData: IFieldData[] = [];
        for (let i = 1; i < maxWorldNumber + 1; i++) {
            const worldFieldData: IFieldData[] = getWorldFieldData(i);

            fieldData = worldFieldData ? [...fieldData, ...worldFieldData] : fieldData;
        }
        return fieldData;
    }

    export function maxLevel(worldNumber = maxWorld()): number {
        return getWorldLevelLimits(worldNumber).max;
    }

    export function maxWorld(): number {
        if (_maxWorld === undefined) {
            let i: number = 1;
            while (i <= MAX_WORLDS && getWorldData(i) !== undefined) {
                i++;
            }
            _maxWorld = i - 1;
        }
        return _maxWorld;
    }

    export function getLevelGoal(levelNumber: number): IWinCriteriaData[] {
        return [
            {
                type: levelsData.goldGoalLevels.includes(levelNumber)
                    ? WinCriteriaType.CLEAR_GOLD
                    : WinCriteriaType.CLEAR_ALL,
            },
        ];
    }

    export function isHardLevel(levelNumber: number): boolean {
        return levelsData.hardLevels.includes(levelNumber);
    }
}
